import { Checkbox } from '@/components/atoms/Checkbox';
import FieldWrapper from '@/components/molecules/FormFields/FormFieldWrapper';
import { makeIdFromName } from '@/lib/string';

import styles from './CheckboxField.module.css';

import type { CheckboxFieldProps } from './CheckboxField.types';
import type { ControllerFieldState, ControllerRenderProps, Path } from 'react-hook-form';

export default function CheckboxField<T extends Record<string, unknown>>({
  helperText,
  label,
  labelVariant = 'light',
  name,
  rules,
  ...rest
}: CheckboxFieldProps<T>) {
  return (
    <FieldWrapper<T> {...rest} name={name} rules={rules} labelVariant={labelVariant}>
      {(field: ControllerRenderProps<T, Path<T>> & { id?: string }, fieldState: ControllerFieldState) => {
        const { value, ...inputProps } = field;
        // Build stable ids for input, helper and error
        const baseId = field.id ?? makeIdFromName(String(name));
        const helperId = helperText ? `${baseId}-helper` : undefined;
        const errorId = `${baseId}-error`;
        const describedBy = fieldState.error ? errorId : helperId;
        const isRequired = Boolean(rules?.required);

        return (
          <div className={styles.fieldContainer}>
            <Checkbox
              {...inputProps}
              aria-checked={Boolean(value)}
              aria-describedby={describedBy}
              aria-invalid={fieldState.invalid ? 'true' : 'false'}
              aria-required={isRequired ? 'true' : undefined}
              checked={Boolean(value)}
              className={styles.checkbox}
              id={baseId}
              label={label}
            />

            {/* helper text (not shown if error present) */}
            {helperText && !fieldState.error && (
              <div id={helperId} className={styles.helperText} aria-hidden={fieldState.error ? 'true' : 'false'}>
                {helperText}
              </div>
            )}

            {/* error message exposed to AT via role="alert" and aria-describedby */}
            {fieldState.error && (
              <div id={errorId} role="alert" className={styles.errorText}>
                {String(fieldState.error.message ?? 'Invalid value')}
              </div>
            )}
          </div>
        );
      }}
    </FieldWrapper>
  );
}
